<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  start: String,
  end: String,
  methods: Array,
  min: [String, Number],
  max: [String, Number],
  customer: String,
  supplier: String,
  product: String,
  methodOptions: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  "update:start",
  "update:end",
  "update:methods",
  "update:min",
  "update:max",
  "update:customer",
  "update:supplier",
  "update:product",
  "generateAi",
  "clear",
]);

// Local state for debounced inputs
const localCustomer = ref(props.customer);
const localSupplier = ref(props.supplier);
const localProduct = ref(props.product);
const localMin = ref(props.min);
const localMax = ref(props.max);

let debounceTimer = null;

function debounceEmit(event, value) {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    emit(event, value);
  }, 500);
}

watch(() => props.customer, (val) => { localCustomer.value = val; });
watch(() => props.supplier, (val) => { localSupplier.value = val; });
watch(() => props.product, (val) => { localProduct.value = val; });
watch(() => props.min, (val) => { localMin.value = val; });
watch(() => props.max, (val) => { localMax.value = val; });
</script>

<template>
  <section class="panel filters-advanced">
    <div class="panel-head"><b>Filtros avanzados</b></div>
    <div class="filters-grid">
      <div class="field">
        <label class="label">Desde</label>
        <div class="control">
          <span class="material-symbols-rounded icon">calendar_month</span>
          <input type="date" :value="start" @change="emit('update:start', $event.target.value)"
            class="analytics-filter-input" />
        </div>
      </div>
      <div class="field">
        <label class="label">Hasta</label>
        <div class="control">
          <span class="material-symbols-rounded icon">calendar_month</span>
          <input type="date" :value="end" @change="emit('update:end', $event.target.value)"
            class="analytics-filter-input" />
        </div>
      </div>
      <div class="field">
        <label class="label">Métodos de pago</label>
        <div class="control">
          <span class="material-symbols-rounded icon">payments</span>
          <select multiple :value="methods" @change="
            emit(
              'update:methods',
              Array.from($event.target.selectedOptions, (o) => o.value)
            )
            " class="analytics-filter-select-multiple">
            <option v-for="m in methodOptions" :key="m" :value="m">
              {{ m }}
            </option>
          </select>
        </div>
      </div>
      <div class="field">
        <label class="label">Monto mínimo</label>
        <div class="control">
          <span class="material-symbols-rounded icon">attach_money</span>
          <input type="number" step="0.01" v-model="localMin" @input="debounceEmit('update:min', localMin)"
            class="analytics-filter-input" placeholder="$" />
        </div>
      </div>
      <div class="field">
        <label class="label">Monto máximo</label>
        <div class="control">
          <span class="material-symbols-rounded icon">attach_money</span>
          <input type="number" step="0.01" v-model="localMax" @input="debounceEmit('update:max', localMax)"
            class="analytics-filter-input" placeholder="$" />
        </div>
      </div>
      <div class="field">
        <label class="label">Cliente</label>
        <div class="control">
          <span class="material-symbols-rounded icon">person</span>
          <input v-model="localCustomer" @input="debounceEmit('update:customer', localCustomer)"
            class="analytics-filter-input" placeholder="Buscar cliente" />
        </div>
      </div>
      <div class="field">
        <label class="label">Proveedor</label>
        <div class="control">
          <span class="material-symbols-rounded icon">store</span>
          <input v-model="localSupplier" @input="debounceEmit('update:supplier', localSupplier)"
            class="analytics-filter-input" placeholder="Buscar proveedor" />
        </div>
      </div>
      <div class="field">
        <label class="label">Producto</label>
        <div class="control">
          <span class="material-symbols-rounded icon">inventory_2</span>
          <input v-model="localProduct" @input="debounceEmit('update:product', localProduct)"
            class="analytics-filter-input" placeholder="Buscar producto" />
        </div>
      </div>
    </div>
    <div class="filters-actions">
      <button class="chip" @click="emit('generateAi')">
        <span class="material-symbols-rounded" aria-hidden="true">auto_awesome</span>
        Analizar con IA
      </button>
      <button class="chip" @click="emit('clear')">
        <span class="material-symbols-rounded" aria-hidden="true">filter_alt_off</span>
        Limpiar filtros
      </button>
    </div>
  </section>
</template>

<style scoped>
.filters-advanced {
  display: grid;
  gap: 0.75rem;
}

.panel-head {
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.65rem;
  row-gap: 0.85rem;
}

.filters-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.field {
  display: grid;
  gap: 0.25rem;
  min-width: 0;
}

.label {
  color: var(--muted);
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.control {
  position: relative;
  min-width: 0;
}

.control .icon {
  position: absolute;
  left: 6px;
  top: 6px;
  color: var(--accent);
  font-size: 14px;
  pointer-events: none;
  z-index: 1;
}

.analytics-filter-input,
.analytics-filter-select-multiple {
  border: 1px solid var(--border) !important;
  background: var(--surface) !important;
  border-radius: 6px !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
  width: 100% !important;
  max-width: 100% !important;
  color: var(--text) !important;
  font-family: inherit !important;
  font-size: 0.8rem !important;
  line-height: 1.2 !important;
  transition: all 0.2s ease !important;
  box-sizing: border-box !important;
}

.analytics-filter-input {
  height: 28px !important;
  min-height: 28px !important;
  max-height: 28px !important;
  padding: 0 0.5rem 0 1.6rem !important;
  margin: 0 !important;
}

.analytics-filter-input::placeholder {
  color: var(--muted) !important;
  opacity: 0.5 !important;
  font-size: 0.75rem !important;
}

/* Date Input Specific Styles */
.analytics-filter-input[type="date"] {
  color-scheme: light;
  cursor: pointer;
}

.analytics-filter-input[type="date"]::-webkit-calendar-picker-indicator {
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
  filter: invert(0.5) sepia(1) saturate(5) hue-rotate(230deg);
}

.analytics-filter-input[type="date"]::-webkit-calendar-picker-indicator:hover {
  opacity: 1;
  filter: invert(0.4) sepia(1) saturate(6) hue-rotate(230deg);
}

.analytics-filter-input[type="date"]::-webkit-datetime-edit-fields-wrapper {
  padding: 0;
}

.analytics-filter-input[type="date"]::-webkit-datetime-edit-text {
  padding: 0 0.2em;
  color: var(--muted);
}

.analytics-filter-input[type="date"]::-webkit-datetime-edit-month-field,
.analytics-filter-input[type="date"]::-webkit-datetime-edit-day-field,
.analytics-filter-input[type="date"]::-webkit-datetime-edit-year-field {
  padding: 0 0.1em;
  color: var(--text);
  font-weight: 500;
}

.analytics-filter-input[type="date"]::-webkit-datetime-edit-month-field:focus,
.analytics-filter-input[type="date"]::-webkit-datetime-edit-day-field:focus,
.analytics-filter-input[type="date"]::-webkit-datetime-edit-year-field:focus {
  background: rgba(106, 92, 245, 0.15);
  color: var(--accent);
  border-radius: 3px;
}

.analytics-filter-select-multiple {
  height: 55px !important;
  min-height: 55px !important;
  max-height: 55px !important;
  padding: 0.3rem 0.4rem 0.3rem 1.6rem !important;
  overflow-y: auto !important;
  cursor: pointer !important;
  margin: 0 !important;
}

.analytics-filter-select-multiple option {
  padding: 0.2rem !important;
  font-size: 0.8rem !important;
}

.analytics-filter-input:focus,
.analytics-filter-select-multiple:focus {
  outline: none !important;
  box-shadow: 0 0 0 2px rgba(106, 92, 245, 0.12) !important;
  border-color: rgba(106, 92, 245, 0.35) !important;
}

.filters-advanced .chip {
  border-radius: 999px;
}

.filters-actions .chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  padding: 0.45rem 0.75rem;
}

.filters-actions .chip .material-symbols-rounded {
  color: var(--accent);
  font-size: 15px;
}

.chip {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 10px;
  padding: 0.4rem 0.65rem;
  cursor: pointer;
  color: var(--text);
  font-family: inherit;
  font-weight: 500;
  transition: all 0.15s ease;
}

.chip:hover {
  background: rgba(106, 92, 255, 0.08);
  border-color: rgba(106, 92, 255, 0.3);
  transform: translateY(-1px);
}

@media (max-width: 1200px) {
  .filters-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 900px) {
  .filters-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .filters-grid {
    grid-template-columns: 1fr;
  }

  .filters-actions {
    flex-direction: column;
  }

  .filters-actions .chip {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .filters-advanced {
    padding: 0.75rem;
    gap: 0.65rem;
  }

  .panel-head {
    font-size: 0.85rem;
  }

  .filters-grid {
    gap: 0.55rem;
    row-gap: 0.75rem;
  }

  .label {
    font-size: 0.65rem;
  }

  .analytics-filter-input {
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
    font-size: 0.85rem !important;
  }

  .analytics-filter-select-multiple {
    height: 60px !important;
    min-height: 60px !important;
    max-height: 60px !important;
    font-size: 0.85rem !important;
  }

  .filters-actions {
    margin-top: 0.15rem;
    gap: 0.4rem;
  }

  .filters-actions .chip {
    font-size: 0.75rem;
    padding: 0.5rem 0.65rem;
  }

  .filters-actions .chip .material-symbols-rounded {
    font-size: 14px;
  }
}
</style>
